/**
 * The media library as pe3d slots see it: which library type a slot kind
 * takes, listing a company's files of that type, uploading new ones, and the
 * reference a slot stores — the same `@server:<id>` (and pair objects for
 * atlas / bitmap font / Spine) a versionedPlayEngine version stores, so a file
 * uploaded here is the same record the other editors see.
 *
 * Files are listed and uploaded per bucket, as in the other editors: a pe3d
 * variant keeps the bucket its Assets tab works in (`dashboardMeta.bucket`).
 *
 * The server reads and checks the files (controller/pe3d/librarySource.js);
 * nothing here looks inside them.
 */

import _ from "lodash";
import { getServerUri } from "./uriUtility";
import { NO_BUCKET, normalizeBucket } from "./playEngineAssetUtils";

export const BUNDLE_REF = "@bundle:";
const SERVER_REF = "@server:";

const IMAGE = ".png,.jpg,.jpeg,.webp";
const imageRe = /\.(png|jpe?g|webp)$/i;

/**
 * Slot kind → library type. `parts` (pair kinds) maps each reference key to
 * the upload `variant` the server records, the descriptor first.
 */
export const LIBRARY = {
  model: { label: "3D model", find: "glb/find", upload: "glb/uploadGlb", accept: ".glb" },
  image: { label: "image", find: "media/find", upload: "media/uploadImage", accept: IMAGE },
  texture: { label: "texture", find: "media/find", upload: "media/uploadImage", accept: IMAGE },
  audio: { label: "sound", find: "sound/find", upload: "sound/uploadSound", accept: ".m4a,.mp3,.ogg,.wav" },
  video: { label: "video", find: "video/find", upload: "video/uploadVideo", accept: ".mp4,.webm" },
  font: { label: "font", find: "font/find", upload: "font/uploadFont", accept: ".ttf,.otf,.woff,.woff2" },
  data: { label: "JSON", find: "jsonData/find", upload: "jsonData/uploadJson", accept: ".json" },
  atlas: {
    label: "atlas", find: "atlas/find", upload: "atlas/uploadAtlas", accept: `.json,${IMAGE}`, group: "animationSet",
    parts: { json: "JSON", image: "image" }, hint: "the TexturePacker .json and its image",
  },
  bitmapFont: {
    label: "bitmap font", find: "bitmapFont/find", upload: "bitmapFont/uploadBitmapFont", accept: `.fnt,.xml,${IMAGE}`, group: "fontSet",
    parts: { xml: "xml", image: "image" }, hint: "the .fnt (or .xml) and its page image",
  },
  spine: {
    label: "Spine", find: "spine/find", upload: "spine/uploadSpine", accept: `.json,.atlas,${IMAGE}`, group: "animationSet",
    parts: { json: "JSON", atlas: "atlasTxt", image: "image" }, hint: "the Spine .json, its .atlas and the page image",
  },
};

export const takesLibraryFiles = (kind) => Boolean(LIBRARY[kind]);

/**
 * The files a multi-file kind is uploaded as, one "+" button each on the slot card
 * (components/pe3d/pe3dSlotCard.js) — as the snapshot editor's Assets tab asks for an atlas's
 * "+ IMG" and "+ JSON". `variant` is the server's role for the file (LIBRARY parts).
 */
export const UPLOAD_PARTS = {
  atlas: [
    { variant: "image", button: "IMG", title: "The atlas image", accept: IMAGE },
    { variant: "JSON", button: "JSON", title: "The atlas JSON (TexturePacker)", accept: ".json" },
  ],
  bitmapFont: [
    { variant: "image", button: "IMG", title: "The font's page image", accept: IMAGE },
    { variant: "xml", button: "XML", title: "The font file (.fnt or .xml)", accept: ".fnt,.xml" },
  ],
  spine: [
    { variant: "image", button: "IMG", title: "The atlas page image", accept: IMAGE },
    { variant: "JSON", button: "JSON", title: "The skeleton JSON", accept: ".json" },
    { variant: "atlasTxt", button: "ATLAS", title: "The Spine atlas (.atlas)", accept: ".atlas,.txt" },
  ],
};

// Where the server keeps each library's thumbnails (the static mounts in server/index.js).
const THUMB_FOLDER = { image: "images", texture: "images", atlas: "atlases", bitmapFont: "bitmapFonts", spine: "spines" };
const thumbOf = (kind, record) => (THUMB_FOLDER[kind] && record && record.thumbFile ? `${THUMB_FOLDER[kind]}/${record.thumbFile}` : null);

const accessToken = () => localStorage.getItem("session.accessToken");
const stem = (name) => String(name || "").replace(/\.[^/.]+$/, "");

/** The id(s) a slot value names, whatever its shape. */
export function refIds(ref) {
  const idOf = (value) => (typeof value === "string" && value.startsWith(SERVER_REF) ? value.slice(SERVER_REF.length) : null);
  if (typeof ref === "string") return idOf(ref) ? [idOf(ref)] : [];
  if (ref && typeof ref === "object") return Object.values(ref).map(idOf).filter(Boolean);
  return [];
}

export const isBundleRef = (ref) => typeof ref === "string" && ref.startsWith(BUNDLE_REF);
export const isLibraryRef = (ref) => refIds(ref).length > 0;

function recordLabel(record) {
  return record.uploadedFileName || record.name || record.fontFamily || record.file || String(record._id);
}

/**
 * The company's library files a slot of this kind can take, every bucket:
 * [{ ref, label, bucket, thumb, records }]. Pair kinds are grouped into
 * complete sets; a video link (not a file) is left out, a playable cannot
 * carry one.
 */
export async function listLibrary(kind) {
  const library = LIBRARY[kind];
  const response = await fetch(getServerUri(library.find), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ accessToken: accessToken() }),
  });
  if (!response.ok) throw new Error(`${library.find}: ${response.status}`);
  const body = await response.json();
  const records = (Array.isArray(body) ? body : body.images || []).filter((record) => record.variant !== "url");

  if (!library.parts) {
    return records
      .filter((record) => new RegExp(`(${library.accept.split(",").map((ext) => _.escapeRegExp(ext)).join("|")})$`, "i").test(record.file || ""))
      .map((record) => ({
        ref: `${SERVER_REF}${record._id}`,
        label: recordLabel(record),
        bucket: normalizeBucket(record.bucket),
        thumb: thumbOf(kind, record),
        records: [record],
      }));
  }

  const sets = _.groupBy(records.filter((record) => record[library.group]), library.group);
  const out = [];
  for (const members of Object.values(sets)) {
    const ref = {};
    for (const [key, variant] of Object.entries(library.parts)) {
      const record = members.find((item) => String(item.variant || "").toLowerCase() === variant.toLowerCase());
      if (record) ref[key] = `${SERVER_REF}${record._id}`;
    }
    if (Object.keys(ref).length !== Object.keys(library.parts).length) continue;
    const image = members.find((item) => String(item.variant || "").toLowerCase() === "image");
    const shown = image || members[0];
    out.push({ ref, label: recordLabel(shown), bucket: normalizeBucket(shown.bucket), thumb: thumbOf(kind, image), records: members });
  }
  return out;
}

/** Which upload role each picked file plays for a pair kind; throws with what is missing. */
export function assignRoles(kind, files) {
  const library = LIBRARY[kind];
  const roles = [];
  for (const file of files) {
    const name = file.name.toLowerCase();
    if (imageRe.test(name)) roles.push({ file, variant: "image" });
    else if (kind === "spine" && /\.(atlas|txt)$/.test(name)) roles.push({ file, variant: "atlasTxt" });
    else if (kind === "bitmapFont" && /\.(fnt|xml)$/.test(name)) roles.push({ file, variant: "xml" });
    else if (kind !== "bitmapFont" && name.endsWith(".json")) roles.push({ file, variant: "JSON" });
    else throw new Error(`${file.name} is not part of a ${library.label}`);
  }
  for (const variant of Object.values(library.parts)) {
    const count = roles.filter((role) => role.variant === variant).length;
    if (count === 0) throw new Error(`Pick ${library.hint} together.`);
    if (count > 1 && variant !== "image") throw new Error(`Only one ${variant} file per ${library.label}.`);
  }
  return roles;
}

/**
 * Upload into the company's library and return the reference the slot stores.
 * @param {string} kind
 * @param {File[]} files  one file, or a pair kind's files picked together
 * @param {string} [bucket]  the variant's bucket
 */
export async function uploadToLibrary(kind, files, bucket = NO_BUCKET) {
  const library = LIBRARY[kind];
  const form = new FormData();
  form.append("accessToken", accessToken());
  form.append("bucket", normalizeBucket(bucket));
  form.append("componentType", null);

  if (library.parts) {
    const roles = assignRoles(kind, files);
    const descriptor = roles.find((role) => role.variant !== "image") || roles[0];
    for (const role of roles) {
      form.append("variant", role.variant);
      form.append("files", role.file);
    }
    form.append("uploadedFileName", stem(descriptor.file.name));
  } else {
    const file = files[0];
    form.append("files", file);
    form.append("uploadedFileName", stem(file.name));
    form.append("name", stem(file.name));
    if (kind === "font") form.append("fontFamily", stem(file.name));
    if (kind === "video") {
      form.append("variant", "path");
      form.append("filename", file.name);
    }
  }

  const response = await fetch(getServerUri(library.upload), { method: "POST", body: form });
  const text = await response.text();
  if (!response.ok) throw new Error(`Upload failed: ${text}`);
  const records = _.get(JSON.parse(text), "records", []);
  if (!records.length) throw new Error("Upload returned no records");

  if (!library.parts) return `${SERVER_REF}${records[0]._id}`;
  const ref = {};
  for (const [key, variant] of Object.entries(library.parts)) {
    const record = records.find((item) => String(item.variant || "").toLowerCase() === variant.toLowerCase());
    if (!record) throw new Error(`Upload returned no ${variant} record`);
    ref[key] = `${SERVER_REF}${record._id}`;
  }
  return ref;
}

// ── The Assets tab's bookkeeping (components/pe3d/containerPe3dAssets.js) ──

/** Files the release ships for a slot: whatever the game's own variants put in it. */
export function slotChoices(bundle, slot) {
  const files = new Set();
  for (const item of bundle.variants || []) if (item.assets && item.assets[slot]) files.add(item.assets[slot]);
  return [...files].sort();
}

/**
 * Every bucket the company's files sit in plus the ones the variant names
 * (sorted, without "no bucket"), and per kind how many files each holds.
 */
export function bucketsOf(libraries, storedBuckets = {}) {
  const countsByKind = {};
  const names = new Set(Object.values(storedBuckets));
  for (const [kind, items] of Object.entries(libraries)) {
    const counts = {};
    for (const item of items || []) {
      counts[item.bucket] = (counts[item.bucket] || 0) + 1;
      names.add(item.bucket);
    }
    countsByKind[kind] = counts;
  }
  names.delete(NO_BUCKET);
  return { buckets: [...names].filter(Boolean).sort((a, b) => a.localeCompare(b)), countsByKind };
}

/** A slot's bucket: the one the variant keeps for it, else the bucket of the library file it uses. */
export function slotBucket(slot, value, storedBuckets, items) {
  if (storedBuckets[slot]) return storedBuckets[slot];
  const used = value && (items || []).find((item) => JSON.stringify(item.ref) === JSON.stringify(value));
  return used ? used.bucket : NO_BUCKET;
}

/** Pretty JSON and a one-line summary, or the raw text and why it did not parse. */
export function describeJson(text) {
  try {
    const value = JSON.parse(text);
    const kind = Array.isArray(value) ? `array of ${value.length}` : value && typeof value === "object" ? `object with ${Object.keys(value).length} keys` : typeof value;
    return { text: JSON.stringify(value, null, 2), summary: kind, error: null };
  } catch (err) {
    return { text, summary: null, error: err.message };
  }
}
